import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import Input from './atoms/Input';
import {
  displayUserPermissions, userPermissionsArray, UserRole, UserType,
} from '../models/user';
import Select from './atoms/Select/Select';
import Button from './atoms/Button';
import { RootState, useCreateInvitationMutation, useGetSellerGroupsQuery } from '../store';
import { toastSuccess } from '../utils/toasts';
import Chip from './atoms/Chip';
import { CreateInvitationDTO, EventUserPermissions } from '../models/invitation';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import isAllowed from '../utils/isAllowed';

interface Props {
  businessId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  email: string;
  permissions: UserRole;
  seller_commission_rate?: number;
  selected_events?: string[];
  seller_group_id?: string;
}

const noSpaces = (value: string) => !/\s/.test(value);
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validationSchema = yup.object<FormData>().shape({
  email: yup
    .string()
    .required('Debes ingresar un correo')
    .matches(emailRegex, 'Este correo no está bien')
    .test('no-spaces', 'No se permiten espacios', noSpaces),
  permissions: yup
    .mixed<UserRole>()
    .required('Debes seleccionar una opción')
    .oneOf(userPermissionsArray, 'La opción seleccionada no es válida'),
  seller_commission_rate: yup
    .number()
    .when('permissions', {
      is: 'SELLER',
      then: (schema) => schema
        .required('Debes ingresar una comisión')
        .min(0, 'La comisión no puede ser negativa')
        .max(100, 'La comisión no puede ser mayor a 100'),
    }),
  seller_group_id: yup.string(),
  selected_events: yup
    .array()
    .of(yup.string().required())
    .when('permissions', {
      is: 'SELLER',
      then: (schema) => schema
        .required('Debes seleccionar al menos un evento'),
    }),
});

const InviteUser: React.FC<Props> = ({ businessId, onSuccess, onCancel }) => {
  const form = useForm<FormData>({
    resolver: yupResolver<FormData>(validationSchema),
    defaultValues: {
      email: '',
      permissions: undefined,
      selected_events: [],
      seller_commission_rate: undefined,
    },
  });

  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const { setValue } = form;
  const userData = useSelector((state: RootState) => state.auth.user);
  const { data: sellerGroups } = useGetSellerGroupsQuery({ businessId: businessId || '' }, { skip: !businessId });

  const { business, businessLoading } = useGetSelectedBusiness();

  const {
    control, handleSubmit, formState, watch,
  } = form;
  const permissions = watch('permissions');

  const [createInvitation, createInvitationResults] = useCreateInvitationMutation();

  if (!business && !businessLoading) {
    throw new Error('no business selected');
  }

  const formToDto = (formData: FormData): CreateInvitationDTO => ({
    email: formData.email,
    permissions: formData.permissions,
    invitation_events: selectedEvents ? selectedEvents.map((e) => ({
      event_id: e,
      permissions: formData.permissions === UserRole.COPRODUCER ? EventUserPermissions.COPRODUCER : EventUserPermissions.SELLER,
      seller_comission_rate: formData.seller_commission_rate ? formData.seller_commission_rate / 100 : 0,
      seller_group_id: formData.seller_group_id,
    })) : undefined,
  });

  const handleSendInvite = async () => {
    try {
      const formData = form.getValues();
      console.log('dto', formToDto(formData));
      const result = await createInvitation({
        businessId,
        dto: formToDto(formData),
      });
      if ('data' in result) {
        toastSuccess('Invitación enviada con éxito');
        onSuccess();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div>
      <form className="w-full h-full" onSubmit={handleSubmit(handleSendInvite)}>
        <div className="mb-5">
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Input
                label="Correo electrónico"
                labelAbove={true}
                id="email"
                {...field}
                onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
                errMsg={formState.errors.email?.message}
              />
            )}
          />
        </div>
        <div className="mb-5">
          <Controller
            name="permissions"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <Select
                label="Nivel de permisos"
                id="permissions"
                options={userPermissionsArray
                  .filter((p) => {
                    if (p === 'USER') {
                      return false;
                    }
                    if (userData?.type === UserType.ROOT) {
                      return true;
                    }
                    return !isAllowed(userData, [UserRole.EDITOR, UserRole.COPRODUCER], businessId) || p !== 'ADMIN';
                  })
                  .map((p) => ({
                    label: displayUserPermissions[p as keyof typeof displayUserPermissions],
                    value: p,
                  }))}
                {...field}
              />
            )}
          />
        </div>

        {(permissions === UserRole.SELLER || permissions === UserRole.COPRODUCER) && (
          <>
            {permissions === UserRole.SELLER && <div className="mb-5">
              <Controller
                name="seller_commission_rate"
                control={control}
                defaultValue={undefined}
                render={({ field }) => (
                  <Input
                    label="Comisión del vendedor (%)"
                    labelAbove={true}
                    id="seller_commission_rate"
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    errMsg={formState.errors.seller_commission_rate?.message}
                  />
                )}
              />
            </div>}
            <div className="mb-5">
              <Controller
                name="selected_events"
                control={control}
                render={() => (
                  <div>
                    <label className="block mb-2">Eventos asignados</label>
                    <Select
                      label="Seleccionar evento"
                      id="event-select"
                      value=""
                      onChange={(value) => {
                        const eventId = value.target.value;
                        if (!selectedEvents.includes(eventId)) {
                          const newSelectedEvents = [...selectedEvents, eventId];
                          setSelectedEvents(newSelectedEvents);
                        }
                      }}
                      options={business?.events
                        .filter((event) => !selectedEvents.includes(event.id))
                        .map((event) => ({
                          label: event.name,
                          value: `${event.id}`,
                        })) ?? []}
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {business?.events
                        .filter((event) => selectedEvents.includes(event.id))
                        .map((event) => (
                          <Chip
                            key={event.id}
                            label={event.name}
                            onDelete={() => {
                              const newSelectedEvents = selectedEvents.filter((id) => id !== event.id);
                              setSelectedEvents(newSelectedEvents);
                              setValue('selected_events', newSelectedEvents);
                            }}
                          />
                        ))}
                    </div>
                    {formState.errors.selected_events && (
                      <span className="text-red-500 text-sm">
                        {formState.errors.selected_events.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
            {(sellerGroups && sellerGroups.length > 0) && <div className="mb-5">
              <Controller
                name="seller_group_id"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Grupo de vendedores"
                    id="seller_group_id"
                    options={sellerGroups.map((group) => ({
                      label: group.name,
                      value: group.id,
                    }))}
                    {...field}
                  />
                )}
              />
            </div>}
          </>
        )}

        <div className="flex flex-row gap-2">
          <Button
            className="w-full"
            variant="secondary"
            type='button'
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            className="w-full"
            variant="primary"
            type='submit'
            loading={createInvitationResults.isLoading}
          >
            Enviar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InviteUser;
